import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { ArrowLeft, Link2, Loader2, Sparkles, BookOpen, HelpCircle } from 'lucide-react';

const subjectIcons = { Math: '📐', Science: '🧬', English: '📖', 'Social Science': '🌍', Hindi: '🔤', Physics: '⚛️', Chemistry: '🧪', Biology: '🧬' };

export default function VideoExtractor({ params, onNavigate }) {
  const { api, user } = useAuth();
  const [videoUrl, setVideoUrl] = useState('');
  const [subject, setSubject] = useState(params?.subject || 'Math');
  const [chapterNum, setChapterNum] = useState(params?.chapter || 1);
  const [extracting, setExtracting] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedIdea, setExpandedIdea] = useState(null);
  const [chapters, setChapters] = useState([]);

  const className = user?.class_name || 'Class 8';
  const subjects = ['Math', 'Science', 'English', 'Social Science', 'Hindi', 'Physics', 'Chemistry', 'Biology'];

  // Fetch chapters for selected subject
  const fetchChapters = useCallback(async () => {
    try {
      const res = await api.get(`/chapters?class_name=${encodeURIComponent(className)}&subject=${encodeURIComponent(subject)}`);
      // Sort chapters by number (NCERT curriculum order)
      const sortedChapters = (res.data || []).sort((a, b) => a.number - b.number);
      setChapters(sortedChapters);
      // Reset to first chapter if current selection is out of range
      if (sortedChapters.length > 0 && !sortedChapters.find(c => c.number === parseInt(chapterNum))) {
        setChapterNum(sortedChapters[0].number);
      }
    } catch (err) {
      console.error('Error fetching chapters:', err);
      setChapters([]);
    }
  }, [api, className, subject, chapterNum]);

  useEffect(() => { fetchChapters(); }, [fetchChapters]);

  const handleExtract = async () => {
    if (!videoUrl.trim()) return;
    setExtracting(true);
    setResult(null);
    try {
      const res = await api.post('/ai/extract-video', {
        video_url: videoUrl,
        subject,
        chapter_number: parseInt(chapterNum),
        class_name: className,
      });
      setResult(res.data);
    } catch (err) {
      console.error(err);
    }
    setExtracting(false);
  };

  return (
    <div className="pb-nav px-4 pt-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button data-testid="back-from-video" onClick={() => onNavigate('dashboard')} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white font-['Outfit']" data-testid="video-title">Video Extractor</h1>
          <p className="text-xs text-slate-500">Paste a video link - get chapter essence</p>
        </div>
      </div>

      {/* Input Section */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 mb-5">
        <div className="space-y-4">
          {/* Video URL */}
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">YouTube / Video Link</label>
            <div className="relative">
              <Link2 className="absolute left-3 top-3.5 w-4 h-4 text-slate-600" />
              <input
                data-testid="video-url-input"
                type="text"
                value={videoUrl}
                onChange={e => setVideoUrl(e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                className="w-full bg-slate-950/50 border border-slate-800 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 rounded-xl h-12 pl-10 pr-4 text-slate-100 placeholder:text-slate-600 outline-none transition-all"
              />
            </div>
          </div>

          {/* Subject + Chapter */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Subject</label>
              <select
                data-testid="video-subject-select"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                className="w-full bg-slate-950/50 border border-slate-800 focus:border-violet-500 rounded-xl h-12 px-3 text-slate-100 outline-none appearance-none"
              >
                {subjects.map(s => <option key={s} value={s}>{subjectIcons[s]} {s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Chapter</label>
              <select
                data-testid="video-chapter-select"
                value={chapterNum}
                onChange={e => setChapterNum(e.target.value)}
                className="w-full bg-slate-950/50 border border-slate-800 focus:border-violet-500 rounded-xl h-12 px-3 text-slate-100 outline-none appearance-none"
              >
                {chapters.map(ch => <option key={ch.number} value={ch.number}>Ch {ch.number}: {ch.name}</option>)}
              </select>
            </div>
          </div>

          <button
            data-testid="extract-btn"
            onClick={handleExtract}
            disabled={extracting || !videoUrl.trim()}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-xl h-12 font-semibold text-sm transition-all glow-violet flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {extracting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Extracting...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Extract Key Ideas
              </>
            )}
          </button>
        </div>
      </div>

      {/* Results */}
      {result && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="video-results">
          {/* Title */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
            <h2 className="text-lg font-bold text-white font-['Outfit'] mb-1">{result.data?.video_title}</h2>
            <p className="text-xs text-slate-500">{result.data?.duration_estimate} - {result.subject} {result.class_name}</p>
            {result.data?.summary && (
              <p className="text-sm text-slate-300 mt-3 bg-violet-500/5 border border-violet-500/10 rounded-lg p-3">{result.data.summary}</p>
            )}
          </div>

          {/* Key Ideas - TikTok style cards */}
          {result.data?.key_ideas?.map((idea, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
              data-testid={`video-idea-${i}`}
              className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 hover:border-cyan-500/30 transition-all"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-cyan-600/20 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-cyan-400">{i + 1}</span>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1">{idea.title}</h3>
                  <p className="text-xs text-slate-400">{idea.explanation}</p>
                  {idea.timestamp_hint && (
                    <span className="text-[10px] text-cyan-500 mt-2 inline-block bg-cyan-500/10 px-2 py-0.5 rounded-full">
                      {idea.timestamp_hint}
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          ))}

          {/* Formulas/Diagrams */}
          {result.data?.formulas_diagrams?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-3">Key Formulas & Diagrams</h3>
              {result.data.formulas_diagrams.map((f, i) => (
                <div key={i} className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 mb-2" data-testid={`video-formula-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <BookOpen className="w-3 h-3 text-amber-400" />
                    <span className="text-[10px] uppercase text-amber-500">{f.type} - {f.importance}</span>
                  </div>
                  <p className="text-sm text-amber-200 font-mono">{f.content}</p>
                </div>
              ))}
            </div>
          )}

          {/* Quick Quiz */}
          {result.data?.quick_quiz?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <HelpCircle className="w-4 h-4" /> Quick Check
              </h3>
              {result.data.quick_quiz.map((q, i) => (
                <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 mb-2" data-testid={`video-quiz-${i}`}>
                  <p className="text-sm text-white mb-2">{q.question}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {q.options?.map((opt, j) => (
                      <button
                        key={j}
                        onClick={() => setExpandedIdea(expandedIdea === `vq-${i}` ? null : `vq-${i}`)}
                        className={`text-xs p-2 rounded-lg border text-left transition-all ${
                          expandedIdea === `vq-${i}` && j === q.correct
                            ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                            : 'border-slate-700 text-slate-400 hover:border-slate-600'
                        }`}
                      >
                        {String.fromCharCode(65 + j)}. {opt}
                      </button>
                    ))}
                  </div>
                  {expandedIdea === `vq-${i}` && (
                    <p className="text-xs text-emerald-400 mt-2 bg-emerald-500/5 p-2 rounded-lg">{q.explanation}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* XP gained */}
          <div className="text-center text-xs text-violet-400 bg-violet-500/5 border border-violet-500/10 rounded-xl py-3">
            +30 XP | +15 Coins earned for extracting video notes!
          </div>
        </motion.div>
      )}
    </div>
  );
}
